import React, { FunctionComponent, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  DateTimePicker,
  HeaderDisplayKeysWithType,
  InlineEditableTable,
  CheckBoxField,
} from '../components';
import Apis from '../apis';
import { ErrorDispatches } from '../redux/Dispatches';
import { Nullable } from '../types';
import { PHISight, PHIWH, PHIWHSight, SimpleStudent } from '../model';
import { Row, Button, Col,FormCheck } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import { ClassSem, WrapClassBaseLayout } from '../layouts';
import { ApplicationState } from '../redux/States';
import {
  DefaultWHContext,
  WH_CONTEXT_BMI_CODE_KEY,
  WHPropertyFields,
} from './TableHeaders/WHHeader';
import { semesterRange } from '../utils/date';
import {
  denormalizeStudentSight,
  normalizeStudentSight,
  SightPropertyFields,
} from './TableHeaders/SightHeader';

type MetricPageData = SimpleStudent & Nullable<PHIWH> & Nullable<PHISight>;
const transformCheckedBox = (
  property: string,
  v: unknown,
  i: number,
  e?: boolean
) =>
  e ? (
    <CheckBoxField property={property} />
  ) : (
    <FormCheck checked={!!v} readOnly type="checkbox" />
  );
const mapState = (state: ApplicationState, ownProps: ClassSem) => ({
  ...ownProps,
  ...state.auth,
});
const mapDispatch = { ...ErrorDispatches };
const connector = connect(mapState, mapDispatch);

type Props = ConnectedProps<typeof connector>;
const classPHIWHSightPage: FunctionComponent<Props> = ({
  classId,
  year,
  sem,
  catchErrorForModal,
}) => {
  const [students, setStudent] = useState([] as MetricPageData[]);
  const [toggle, setToggle] = useState(false);
  const [wHExamDate, setWHExamDate] = useState<DateTime>();
  const [initialExamDate, setInitialExamDate] = useState<DateTime>();
  const [newExamDate, setNewExamDate] = useState<DateTime>();

  useEffect(() => {
    refreshStudentMetric();
  }, [classId, year, sem, toggle]);

  const displayHeader: HeaderDisplayKeysWithType<MetricPageData>[] = [
    { property: 'seat', display: '座號' },
    { property: 'name', display: '學生' },
    {
      property: 'height',
      display: '身高',
      onRender: (v, i, e) => (e ? WHPropertyFields.height : v),
    },
    {
      property: 'weight',
      display: '體重',
      onRender: (v, i, e) => (e ? WHPropertyFields.weight : v),
    },
    {
      property: 'isDilated',
      display: '散瞳治療',
      onRender: transformCheckedBox.bind(null, 'isDilated'),
    },
    {
      property: 'sight0R',
      display: '裸視右',
      onRender: (value, i, e) => {
        if (e) return SightPropertyFields.sight0R;
        if (typeof value === 'number') {
          if (value === -1) return '<0.1';
          if (value === -5) return '<0.1\n(全盲)';
          if (value < 0) return value;
          if (value > 0 && value % 1 !== 0) {
            return value.toFixed(1);
          }
          return value.toFixed(1).toString();
        }
        return value as string;
      },
    },
    {
      property: 'sight0L',
      display: '裸視左',
      onRender: (value, i, e) => {
        if (e) return SightPropertyFields.sight0L;
        if (typeof value === 'number') {
          if (value === -1) return '<0.1';
          if (value === -5) return '<0.1\n(全盲)';
          if (value < 0) return value;
          if (value > 0 && value % 1 !== 0) {
            return value.toFixed(1);
          }
          return value.toFixed(1).toString();
        }
        return value as string;
      },
    },
    {
      property: 'sightR',
      display: '戴鏡右',
      onRender: (value: unknown, i, e) => {
        if (e) return SightPropertyFields.sightR;
        if (typeof value === 'number') {
          if (value === -1) return '<0.1';
          if (value === -5) return '<0.1\n(全盲)';
          return value % 1 === 0 ? value.toFixed(1) : value.toString();
        }
        return value as string;
      },
    },
    {
      property: 'sightL',
      display: '戴鏡左',
      onRender: (value: unknown, i, e) => {
        if (e) return SightPropertyFields.sightL;
        if (typeof value === 'number') {
          if (value === -1) return '<0.1';
          if (value === -5) return '<0.1\n(全盲)';
          return value % 1 === 0 ? value.toFixed(1) : value.toString();
        }
        return value as string;
      },
    },
    {
      property: 'examDate',
      display: '測量日',
      style: { width: '20%' },
      onRender: (v: unknown, i, e, c) => {
        if (e) {
          const [start, end] = semesterRange(year, sem);
          return WHPropertyFields.examDate(start, end, c.allValues);
        }
        return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : null;
      },
    },
    {
      property: 'bmi',
      display: 'BMI',
      onRender: (value: unknown) => {
        if (typeof value === 'number') {
          return Math.round(value * 10) / 10; // 四捨五入到小數點後一位
        }
        return value;
      },
    },
    {
      property: 'bmiCode',
      display: '體位判讀',
      onRender: (v: unknown, i, e, c) => {
        const {
          [WH_CONTEXT_BMI_CODE_KEY]: Codes = DefaultWHContext[
          WH_CONTEXT_BMI_CODE_KEY
          ],
        } = (c?.context || DefaultWHContext) as typeof DefaultWHContext;
        return (typeof v === 'string' ? Codes[v] : (v as string)) || '';
      },
    },
  ];

  function refreshStudentMetric() {
    if (classId && year && sem) {
      Apis.getClassStudentWHSight(classId, year, sem)
        .then((d) => {
          const datas: MetricPageData[] = d.map((m) => {
            const { examDate, ...sight } = m.sight || {};
            return {
              ...m,
              ...sight,
              ...m.phiWH,
              sex: '',
            };
          });
          setStudent(datas);
        })
        .catch(catchErrorForModal);
    } else {
      setStudent([]);
    }
  }

  function onDelete(sight: MetricPageData) {
    toast
      .promise(Apis.deleteStudentWHSight(sight.pid, year, sem), {
        pending: '資料刪除中......',
        success: '資料刪除成功！',
        error: '資料刪除失敗！請查看錯誤資訊。',
      })
      .then(() => setToggle(!toggle))
      .catch(catchErrorForModal);
  }

  async function onUpdateStudentMetric(pid: string, metric: PHIWHSight) {
    toast.dismiss();
    toast
      .promise(Apis.InsertStudentWHSight(pid, metric), {
        pending: '資料上傳中......',
        success: '上傳成功！',
        error: '上傳失敗！請查看錯誤資訊。',
      })
      .then(() => {
        setToggle(!toggle);
      })
      .catch(catchErrorForModal);
  }

  function updateWHExamDate() {
    if (classId && wHExamDate) {
      Apis.UpdateWHExamDate(classId, year, sem, wHExamDate, wHExamDate)
        .then((count) => {
          toast.success(`資料更新成功！共 ${count} 筆資料已更新。`);
          setToggle(!toggle);
        })
        .catch(catchErrorForModal);
    }
  }

  function updateSelectedWHExamDate() {
    if (classId && initialExamDate && newExamDate) {
      Apis.UpdateSelectedWHExamDate(
        classId,
        year,
        sem,
        initialExamDate,
        newExamDate
      )
        .then((count) => {
          toast.success(`資料更新成功！共 ${count} 筆資料已更新。`);
          setToggle(!toggle);
        })
        .catch(catchErrorForModal);
    }
  }

  const rowClassName = 'pb-3 align-items-center';
  return (
    <React.Fragment>
      {students.length ? (
        <>
          <Row className="mb-3">
            體位測量日設定：
            <Col sm={2} className="mr-3">
              <DateTimePicker
                containerClass="wid-140 mr-2"
                value={wHExamDate}
                onChange={(date) => {
                  setWHExamDate(date);
                }}
              />
            </Col>
            <Button
              disabled={wHExamDate ? false : true}
              onClick={updateWHExamDate}
            >
              設定測量日
            </Button>
          </Row>
          <hr />
          <Row className="mb-3">
            <Col sm={12}>體位測量日修改</Col>
            請輸入原設定日期：
            <Col sm={2} className="mr-2">
              <DateTimePicker
                containerClass="wid-140 mr-2"
                value={initialExamDate}
                onChange={(date) => {
                  setInitialExamDate(date);
                }}
              />
            </Col>
            請輸入欲修改日期：
            <Col sm={2} className="mr-3">
              <DateTimePicker
                containerClass="wid-140 mr-2"
                value={newExamDate}
                onChange={(date) => {
                  setNewExamDate(date);
                }}
              />
            </Col>
            <Button
              disabled={initialExamDate ? (newExamDate ? false : true) : true}
              onClick={updateSelectedWHExamDate}
            >
              更新測量日
            </Button>
          </Row>
          <hr />
        </>
      ) : (
        <></>
      )}
      <Row>
        <div
          className="mt-3 p-3"
          style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '6px',
            fontSize: '15px',
            lineHeight: '1.6',
            width: '100%',
            marginBottom:'10px'
          }}
        >
          <div><strong>身高體重視力輸入說明：</strong></div>

          <div>1️⃣ 視力請直接輸入整數，例如 0.8 請輸入 8；視力值＜0.1 請輸入 -1。</div>

          <div>
            2️⃣ 「散瞳治療」是指點長效型散瞳眼藥治療；「散瞳」是指檢驗屈光度數前的短效散瞳。
          </div>

          <div>
            3️⃣ 裸眼視力無法測量代號：-8 戴隱形眼鏡；-7 角膜塑型；-6 雷射治療；-5 全盲
          </div>

          <div>
            4️⃣ 無法表達或在家自學僅輸入裸視右眼為 -9。
          </div>

          <div>
            5️⃣ 「定期檢查」日期格式：1999/01/01，或點兩下顯示萬年曆選單。
          </div>

          <div>
            6️⃣ 如「近視右（左）」或「遠視右（左）」有小數點，
            請將小數點四捨五入至個位數後以整數輸入。
          </div>
          <div>
            7️⃣ 身高體重 無法測量時，請輸入-9(無須輸入測量日)
          </div>
        </div>
      </Row>
      <Row>
        <InlineEditableTable
          fixCol={2}
          values={students}
          autoNext={true}
          headers={displayHeader}
          onValueUpdate={(value: MetricPageData) => {
            const data = {
              ...value,
              ...normalizeStudentSight(value),
              examDate:
                value.weight == -9 || value.height == -9
                  ? null
                  : value.examDate,
              id: 0,
              sem,
              year,
              yearClassId: 0,
            } as PHIWHSight;
            if (data.weight != -9 && data.height != -9 && !data.examDate) {
              toast.error('請填入測量日');
              return;
            }
            onUpdateStudentMetric(value.pid, data);
          }}
          onRowRender={(v) => ({ deleteable: true, editable: true })}
          onValueDelete={onDelete}
          beforeEdit={(s) => ({ ...s, ...denormalizeStudentSight(s) })}
        />
      </Row>
    </React.Fragment>
  );
};

export const ClassPHIWHSightPage = connector(
  WrapClassBaseLayout(classPHIWHSightPage)
);
